// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { TableCell, TableRow } from './ui/table';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  MoreVertical,
  Eye,
  Download,
  Pencil,
  Move,
  Trash2,
  PenLine,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { formatFileSize, formatDate, getFileIcon } from '../lib/utils';
import RetentionBadge from './RetentionBadge';

/**
 * Single file row inside the Documents table.
 *
 * Admin actions (Rubricar / Ver Rubricas) are gated by `isAdminUser` boolean
 * (caller is expected to pass `isAdmin()` already evaluated).
 */
export default function FileRow({
  file,
  index,
  totalFolders,
  areaConfig,
  isAdminUser,
  onPreview,
  onDownload,
  onRename,
  onMove,
  onDelete,
  onSign,
  onViewSignatures,
  canWrite = true,
}) {
  const FileIcon = getFileIcon(file.content_type);

  return (
    <TableRow
      className="cursor-pointer hover:bg-slate-50/80 transition-colors animate-fade-in border-b border-slate-50"
      style={{ animationDelay: `${(totalFolders + index) * 0.03}s` }}
      onClick={() => onPreview(file)}
      data-testid={`file-row-${file.id}`}
    >
      <TableCell className="font-medium py-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${areaConfig.bgColor} transition-transform hover:scale-105`}>
            <FileIcon className={`w-5 h-5 ${areaConfig.color}`} />
          </div>
          <div className="min-w-0">
            <span className="truncate block max-w-[300px] text-slate-900">{file.original_filename}</span>
            {file.relative_path && file.relative_path !== file.original_filename && (
              <span className="text-xs text-slate-400 truncate block mt-0.5">
                {file.relative_path}
              </span>
            )}
            <div className="mt-1"><RetentionBadge retentionUntil={file.retention_until} /></div>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-slate-500 text-sm hidden sm:table-cell">
        {formatFileSize(file.size)}
      </TableCell>
      <TableCell className="text-slate-500 hidden md:table-cell">
        <div className="flex items-center gap-1.5 text-sm">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(file.created_at)}
        </div>
      </TableCell>
      <TableCell className="text-slate-500 text-sm hidden lg:table-cell">
        {file.uploaded_by_name}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 justify-end">
          {isAdminUser && (
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl hover:bg-emerald-50 text-emerald-600 hover:text-emerald-700"
              onClick={(e) => { e.stopPropagation(); onSign(file); }}
              title="Rubricar documento"
              data-testid={`sign-file-btn-${file.id}`}
            >
              <PenLine className="w-4 h-4" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl hover:bg-slate-100"
                data-testid={`file-menu-${file.id}`}
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl shadow-xl border-slate-200/80">
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); onPreview(file); }}
                className="py-2.5 cursor-pointer"
                data-testid={`preview-file-${file.id}`}
              >
                <Eye className="w-4 h-4 mr-2" />
                Visualizar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); onDownload(file); }}
                className="py-2.5 cursor-pointer"
                data-testid={`download-file-${file.id}`}
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {canWrite && (
                <>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onRename(file); }}
                    className="py-2.5 cursor-pointer"
                    data-testid={`rename-file-${file.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Renomear
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onMove(file); }}
                    className="py-2.5 cursor-pointer"
                    data-testid={`move-file-${file.id}`}
                  >
                    <Move className="w-4 h-4 mr-2" />
                    Mover
                  </DropdownMenuItem>
                </>
              )}
              {isAdminUser && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onSign(file); }}
                    className="py-2.5 cursor-pointer text-emerald-600 focus:text-emerald-600"
                    data-testid={`sign-file-${file.id}`}
                  >
                    <PenLine className="w-4 h-4 mr-2" />
                    Rubricar
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onViewSignatures(file); }}
                    className="py-2.5 cursor-pointer"
                    data-testid={`view-signatures-${file.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Ver Rubricas
                  </DropdownMenuItem>
                </>
              )}
              {canWrite && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onDelete(file); }}
                    className="text-red-600 focus:text-red-600 focus:bg-red-50 py-2.5 cursor-pointer"
                    data-testid={`delete-file-${file.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
